import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { AppSettingService } from '../app.service';
import { UserInfoService } from '../user/user-info.service';
import { ITerritories } from '../map/map-api.service';

export interface IUserZone {
	Client: string,
	DisplayName: string,
	AllowSignup: boolean,
	JobProvider: any[],
	AllowAvatarImageChange: boolean,
	AllowCoverImageChange: boolean,
	DefaultAvatarImage: string,
	DefaultCoverImage: string,
	Logo: string,
	IsPublic: boolean,
	IsJAZone: boolean
}

export class UserZone implements IUserZone {
	Client: string = "";
	DisplayName: string = "";
	AllowSignup: boolean = false;
	JobProvider: any[] = null;
	AllowAvatarImageChange: boolean = false;
	AllowCoverImageChange: boolean = false;
	DefaultAvatarImage: string = "";
	DefaultCoverImage: string = "";
	Logo: string = "";
	IsPublic: boolean = false;
	IsJAZone: boolean = false;
}

export interface ISearchZone { 
	ZoneId: string,
	Client: string,
	DisplayName: string
}

export type TEDCImportFrom = "TCS" | "ClassLink" | "Clever";
export type TEDCSyncDirection = "none" | "download";
export type TEDCSyncType = "full" | "change";
export type TZoneJobProvider = {
	Offset: number,
	Total: number,
	Result: string,
	Status: string,
	Description: string,
	Provider: string,
	TimeSpan: number
}

export interface IZone {
	ZoneId: string,
	Client: string,
	DisplayName: string
	AgentKey: string,
	ImportFrom: TEDCImportFrom,
	DefaultPassword: string,
	ThreadSleepInMinute: number,
	SyncDirection: TEDCSyncDirection,
	SyncType: TEDCSyncType,
	ZoneIndex: number,
	AllowSignup: boolean,
	AllowTransferIn: boolean,
	AccessCode: string,
	CSCCollegeIds: { Id: string, Name: string }[],
	JobProvider: TZoneJobProvider[],
	AllowAvatarImageChange: boolean,
	AllowCoverImageChange: boolean,
	DefaultAvatarImage: string,
	DefaultCoverImage: string,
	Logo: string,
	IsPublic: boolean,
	AllowCreateConnection: boolean,
	BoundInstitutions: { InstitutionCode: string, InstitutionName: string }[],
	SendWelcomeEmail: boolean,
	IsJAZone: boolean,
	IsPublicZone: boolean,
	NoSchoolClass: boolean,
	LockGlobalAccountOption: boolean,
	RequireGlobalAccount: boolean
}

export interface IAPIKey {
	ZoneId: string,
	APIKey: string,
	APIKeyUrlEncode: string,
	ConnectionCode: string
}

export interface IDetailZone extends IZone {
	APIKey: IAPIKey,
	Territories: ITerritories;
	JobPostingEnable: boolean;
}

export interface IUpdateImage {
	DefaultImage?: string,
	Base64Image?: string
}

@Injectable({
	providedIn: 'root'
})
export class ZoneAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "Zone";

	saveZone(zone: IZone, enableAnonymousAccess: boolean, connectionCode: string): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "?enableAnonymousAccess=" + enableAnonymousAccess.toString() + "&connectionCode=" + encodeURIComponent(connectionCode),
			zone,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZoneById(zoneId: string): Rx.Observable<IDetailZone> {
		return this.http.get<any>(
			this.url + "/" + encodeURIComponent(zoneId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveProfilePhoto(zoneId: string, image: IUpdateImage): Rx.Observable<any> {
		return this.http.patch<any>(
			this.url + "/" + encodeURIComponent(zoneId) + "/ProfilePhoto",
			image,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveCoverPhoto(zoneId: string, image: IUpdateImage): Rx.Observable<any> {
		return this.http.patch<any>(
			this.url + "/" + encodeURIComponent(zoneId) + "/CoverPhoto",
			image,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSiteLogo(zoneId: string, image: IUpdateImage): Rx.Observable<any> {
		return this.http.patch<any>(
			this.url + "/" + encodeURIComponent(zoneId) + "/SiteLogo",
			image,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	allowCreateConnection(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/AllowCreateConection",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	createZone(agentKey: string): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Add/" + encodeURIComponent(agentKey),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZoneByAgentKey(agentKey: string): Rx.Observable<IZone> {
		return this.http.get<IZone>(
			this.url + "/Add/" + encodeURIComponent(agentKey),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZoneByName(zoneName: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Name/" + encodeURIComponent(zoneName)
		);
	}

	getPublicZone(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/PublicZone"
		);
	}

	searchZone(zoneName: string, limit: number = 100, offset: number = 0): Rx.Observable<ISearchZone[]>  {
		return this.http.get<any>(
			this.url + "/Search?keyword=" + encodeURIComponent(zoneName) + "&limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	// get all zones, use search to get zone with simple info
	getZones(): Rx.Observable<ISearchZone[]> {
		return this.searchZone("", 10000, 0);
	}

	getStatus(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Status",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserZone(): Rx.Observable<IUserZone> {
		return this.http.get<IUserZone>(
			this.url + "/UserZone",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}
